import React from 'react';
import { AssistantMessage, Message } from 'Assistant/Chatbot/types';
import Markdown from 'react-markdown';
import { ChatbotMessageRole } from 'Assistant/Chatbot/enums';
import {
  ChatUIEnhancementContentCard,
  ChatUIEnhancementCategoryCard,
  MappedMessageType,
} from 'Assistant/__generated__/client.schemas';
import { ChatbotFeedback } from '../ChatbotFeedback';
import { AssistantIcon } from '../styles';
import {
  MessageCard,
  MessageText,
  MessageTextWrapper,
  OptionsWrapper,
  UserMessage,
  UserMessageTail,
  UserMessageBubble,
  UserMessageContainer,
  TranscriptionIconContainer,
  TranscriptionIcon,
} from './styles';
import ContentCardsGroup from './Modals/ContentCardsGroup';
import CategoryCardsGroup from './Modals/CategoryPillsGroup';
import MarkdownMessage from './MarkdownMessage';

type ChatbotMessageProps = {
  message: Message;
  conversationId?: string;
};

type ChatbotOptionMessageProps = {
  message: AssistantMessage;
  options: React.ReactNode;
};

export const ChatbotMessage = ({
  message,
  conversationId,
}: ChatbotMessageProps) => {
  const renderTextChunk = (
    chunk: AssistantMessage['source'][number],
    idx: number,
  ) => {
    switch (chunk.kind) {
      case 'text':
        return (
          <MessageText key={idx}>
            <MarkdownMessage>{chunk.value}</MarkdownMessage>
          </MessageText>
        );
      default:
        return (
          <pre key={idx} data-testid="assistant-unknown-chunk">
            {JSON.stringify(chunk, null, 2)}
          </pre>
        );
    }
  };

  const renderMessageContent = () => {
    const nodes: React.ReactNode[] = [];
    const { source } = message as AssistantMessage;

    for (let i = 0; i < source.length; ) {
      const chunk = source[i];
      if (chunk.kind === 'content_card') {
        const groupStart = i;
        const group: AssistantMessage['source'] =
          [] as AssistantMessage['source'];

        while (i < source.length && source[i].kind === 'content_card') {
          group.push(source[i]);
          i++;
        }

        nodes.push(
          <ContentCardsGroup
            key={`content-row-${groupStart}`}
            group={
              group as unknown as {
                kind: 'content_card';
                value: ChatUIEnhancementContentCard;
              }[]
            }
            groupStartKey={groupStart}
          />,
        );
      } else if (chunk.kind === 'category_card') {
        const groupStart = i;
        const group: AssistantMessage['source'] =
          [] as AssistantMessage['source'];

        while (i < source.length && source[i].kind === 'category_card') {
          group.push(source[i]);
          i++;
        }

        nodes.push(
          <CategoryCardsGroup
            key={`category-row-${groupStart}`}
            group={
              group as unknown as {
                kind: 'category_card';
                value: ChatUIEnhancementCategoryCard;
              }[]
            }
            groupStartKey={groupStart}
          />,
        );
      } else {
        nodes.push(renderTextChunk(chunk, i));
        i++;
      }
    }

    return nodes;
  };

  const isVoice = message.type === MappedMessageType.voice;

  return (
    <MessageCard sender={message.role} data-message-id={message.id}>
      {message.role === ChatbotMessageRole.user ? (
        <UserMessageContainer>
          <UserMessageBubble>
            <UserMessage italic={isVoice}>
              <Markdown>{message.content}</Markdown>
            </UserMessage>
            <UserMessageTail $isVoice={isVoice} />
          </UserMessageBubble>
          {isVoice && (
            <TranscriptionIconContainer>
              <TranscriptionIcon />
            </TranscriptionIconContainer>
          )}
        </UserMessageContainer>
      ) : (
        <>
          <AssistantIcon />
          <MessageTextWrapper>
            {renderMessageContent()}
            <ChatbotFeedback
              messageId={message.id}
              conversationId={conversationId}
            />
          </MessageTextWrapper>
        </>
      )}
    </MessageCard>
  );
};

export const ChatbotOptionsMessage = ({
  message,
  options,
}: ChatbotOptionMessageProps) => {
  // Combine only text chunks for options rendering
  const html = message.source
    .filter(c => c.kind === 'text')
    .map(c => c.value)
    .join('\n');

  return (
    <MessageCard sender={ChatbotMessageRole.assistant}>
      <MessageTextWrapper hasOptions={true}>
        <AssistantIcon />
        <MessageText dangerouslySetInnerHTML={{ __html: html }} />
      </MessageTextWrapper>
      <OptionsWrapper>{options}</OptionsWrapper>
    </MessageCard>
  );
};
